import { useSearchParams } from 'react-router';

import type { FormType, WizardStep } from '../types/Wizard.types';

interface UseWizardContinueProps {
  steps: readonly WizardStep[];
  currentStep: number;
  isCurrentStepComplete: boolean;
  values: FormType;
  handlePaymentConfirmation: () => Promise<boolean>;
  setShowErrors: (show: boolean) => void;
  setShowMessage: (show: boolean) => void;
  goNext: (isComplete: boolean, setShowErrors: (show: boolean) => void) => void;
}

interface UseWizardContinueReturn {
  handleContinueClick: () => Promise<void>;
  handleCanvasClose: () => void;
}

/**
 * Hook that encapsulates the continue click and canvas close logic.
 * Handles payment step confirmation, questionnaire warning message logic, and step navigation.
 */
export function useWizardContinue({
  steps,
  currentStep,
  isCurrentStepComplete,
  values,
  handlePaymentConfirmation,
  setShowErrors,
  setShowMessage,
  goNext,
}: UseWizardContinueProps): UseWizardContinueReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  const handleContinueClick = async () => {
    // Handle payment step confirmation
    if (steps[currentStep] === 'payment') {
      // Check if step is complete (including billing address validation if needed)
      if (!isCurrentStepComplete) {
        setShowErrors(true);
        return; // Stay on payment step if validation fails
      }

      const confirmed = await handlePaymentConfirmation();
      if (!confirmed) {
        return; // Stay on payment step if confirmation failed
      }

      // Payment confirmed successfully - proceed to next step
      goNext(true, setShowErrors);
      return;
    }

    if (steps[currentStep] === 'questionnaire') {
      if (isCurrentStepComplete) {
        const hasAnyYes = Object.values(values.questionnaire || {}).some(value => value === 'YES');
        const msg = searchParams.get('msg');

        if (hasAnyYes && msg !== 'true') {
          setShowMessage(true);
          return;
        }
      }
    }

    goNext(isCurrentStepComplete, setShowErrors);
  };

  const handleCanvasClose = () => {
    const params = new URLSearchParams(searchParams);
    params.set('step', 'questionnaire');
    params.set('msg', 'true');

    setSearchParams(params);
    setShowMessage(false);

    // Proceed to next step after acknowledging
    goNext(isCurrentStepComplete, setShowErrors);
  };

  return {
    handleContinueClick,
    handleCanvasClose,
  };
}
